import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, Award, MapPin, Phone, Mail, CheckCircle, ArrowRight,
  ArrowUpRight, Globe2, Building2, Factory, Truck, Cog, Briefcase,
  HardHat, FileCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import PageHeader from '@/components/common/PageHeader';
import ConsultationForm from '@/components/common/ConsultationForm';
import { usePageMeta } from '@/hooks/useSEO';

/**
 * /eastern-europe-recruitment-india
 * ────────────────────────────────────────────────────────────────────────
 * Eastern Europe hub page aggregating Russia + Poland + emerging markets
 * (Czechia, Slovakia, Romania, Hungary). 1,500–1,800 words. Designed as
 * a regional internal-linking node that consolidates authority signals
 * and captures multi-country commercial intent.
 */

const PRIMARY_PHONE = '+91-99588-48998';
const PRIMARY_EMAIL = 'info@zafco.in';
const MEA_REG = 'B-0762/DEL/PER/1000+/5/9148/2015';

const COUNTRY_COMPARISON = [
  { country: 'Russia', visa: 'National Visa Type D + Work Permit', timeline: '30–45 days', sectors: 'Construction, Manufacturing, Oil & Gas, Metallurgy', signal: '1M+ Indian workers projected by 2026', cost: '30–40%' },
  { country: 'Poland', visa: 'Type A / Single Permit / EU Blue Card', timeline: '45 days', sectors: 'Logistics & Warehousing, Manufacturing, Construction', signal: '~1.29M foreign workers (end-2025)', cost: '30–40%' },
  { country: 'Czechia', visa: 'EU Work Permit / Employee Card', timeline: '45–60 days', sectors: 'Manufacturing, Automotive, Engineering', signal: 'Growing — automotive expansion', cost: '25–35%' },
  { country: 'Slovakia', visa: 'EU Work Permit / Temporary Residence', timeline: '45–60 days', sectors: 'Electronics, Automotive, Manufacturing', signal: 'Steady — industrial cluster', cost: '25–35%' },
  { country: 'Romania', visa: 'EU Work Permit / Long-Stay Visa', timeline: '50–70 days', sectors: 'Construction, Logistics, Infrastructure', signal: 'Boom — EU-funded infrastructure', cost: '30–40%' },
];

const FAQS = [
  { q: 'Can ZAFCO handle recruitment for multiple Eastern European countries simultaneously?',
    a: 'Yes. Our Eastern Europe desk runs unified mandates across Russia, Poland and emerging EU markets (Czechia, Slovakia, Romania, Hungary) — single point of accountability with country-specific work-permit and visa handling baked into one project plan.' },
  { q: 'What is the average recruitment timeline for Eastern Europe?',
    a: 'End-to-end mobilization typically runs 30–70 days depending on destination: Russia 30–45 days, Poland ~45 days, Czechia/Slovakia 45–60 days, Romania 50–70 days. Work-permit filing is the longest single stage in each corridor.' },
  { q: 'Do you provide workers for construction projects across Eastern Europe?',
    a: 'Yes. We supply masons, carpenters, steel fixers, shuttering carpenters, electricians, plumbers, scaffolders and structural welders across Russia, Poland and the EU markets — sourced through our pan-India network and trade-tested at regional centres before mobilization.' },
  { q: 'What industries have the highest demand in Eastern Europe?',
    a: 'Logistics & Warehousing (Poland\u2019s deepest gap), Manufacturing (across all five countries — automotive in Czechia/Slovakia, food processing in Poland/Romania), Construction (Russia and Romania), Welding/Fabrication (Polish shipyards, Russian EPC) and Engineering (multi-country EPC mandates).' },
  { q: 'How does ZAFCO manage work permits for different countries?',
    a: 'A dedicated Eastern Europe desk holds in-house expertise on each pathway — Russian Type D + work permit, Polish Type A / Single Permit / EU Blue Card, and the EU Work-Permit / Employee Card route for Czechia, Slovakia and Romania. Documentation, apostille and embassy-stamping are managed for each country individually.' },
  { q: 'Can you provide bulk manpower for mega projects in Eastern Europe?',
    a: 'Yes. We mobilize 100–10,000 worker engagements with rolling weekly tranches — typical for Polish 3PL ramp-ups, Russian EPC plant erection and Romanian infrastructure builds. Sourcing, testing and visa filings run in parallel pipelines to compress overall timelines.' },
  { q: 'What is the cost advantage of Indian workers for Eastern Europe?',
    a: 'Indian sourcing typically delivers 25–40% lower fully-loaded workforce cost vs. Ukrainian, Belarusian or Western-EU alternatives, while maintaining productivity parity and significantly higher long-term retention (2–3 year deployment cycles).' },
  { q: 'Do you offer services for Czechia, Slovakia, Romania and Hungary?',
    a: 'Yes — these are emerging corridors in our active coverage. Czechia and Slovakia are anchored on automotive and electronics manufacturing; Romania is anchored on EU-funded infrastructure and logistics; Hungary on automotive Tier-1 supply. Country-specific commercials are issued on enquiry.' },
];

const EasternEuropeRecruitmentIndiaPage = () => {
  usePageMeta(
    'Eastern Europe Recruitment Agency in India | ZAFCO',
    'MEA-licensed Eastern Europe Recruitment Agency in India — Russia, Poland, Czechia, Slovakia, Romania. Skilled Indian manpower across the corridor.',
    {
      canonical: '/eastern-europe-recruitment-india',
      image: '/social-media/og-international-recruitment.jpg',
      imageAlt: 'Eastern Europe Recruitment Agency in India — ZAFCO supplies skilled Indian manpower across Russia, Poland, Czechia, Slovakia and Romania.',
    },
  );

  useEffect(() => {
    const breadcrumb = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://zafco.in/' },
        { '@type': 'ListItem', position: 2, name: 'International Recruitment',
          item: 'https://zafco.in/international-manpower-recruitment-europe-russia-israel-saudi-arabia' },
        { '@type': 'ListItem', position: 3, name: 'Eastern Europe Recruitment',
          item: 'https://zafco.in/eastern-europe-recruitment-india' },
      ],
    };
    const faq = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: FAQS.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    };
    const service = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Eastern Europe Recruitment Agency in India for Skilled Manpower',
      description: 'MEA-licensed overseas recruitment service supplying skilled Indian manpower across Eastern Europe: Russia, Poland, Czechia, Slovakia, Romania and Hungary.',
      serviceType: 'Multi-Country Overseas Manpower Recruitment',
      provider: { '@id': 'https://zafco.in/#organization' },
      areaServed: [
        { '@type': 'Country', name: 'Russia' },
        { '@type': 'Country', name: 'Poland' },
        { '@type': 'Country', name: 'Czechia' },
        { '@type': 'Country', name: 'Slovakia' },
        { '@type': 'Country', name: 'Romania' },
        { '@type': 'Country', name: 'Hungary' },
        { '@type': 'Country', name: 'India' },
      ],
      audience: { '@type': 'Audience', audienceType: 'Eastern European and multi-country employers seeking skilled Indian manpower' },
      url: 'https://zafco.in/eastern-europe-recruitment-india',
    };
    const inject = (data, id) => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-ee-hub', id);
      el.text = JSON.stringify(data).replace(/<\/(script)/gi, '<\\/$1');
      document.head.appendChild(el);
      return el;
    };
    const els = [inject(breadcrumb, 'breadcrumb'), inject(faq, 'faq'), inject(service, 'service')];
    return () => { els.forEach((el) => el?.remove()); };
  }, []);

  const contactQuery = '?source=eastern-europe-recruitment-india';

  return (
    <div data-testid="eastern-europe-recruitment-india-page">
      <PageHeader
        title="Eastern Europe Recruitment Agency in India for Skilled Manpower"
        subtitle="MEA-licensed multi-country recruitment hub — supplying skilled Indian workforce to Russia, Poland, Czechia, Slovakia and Romania employers."
        breadcrumbs={[
          { label: 'Home', path: '/' },
          { label: 'International Recruitment', path: '/international-manpower-recruitment-europe-russia-israel-saudi-arabia' },
          { label: 'Eastern Europe Recruitment', path: '/eastern-europe-recruitment-india' },
        ]}
      />

      <div className="bg-gradient-to-r from-green-600 to-green-700 text-white py-4">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
            <Shield className="w-8 h-8 flex-shrink-0" aria-hidden="true" />
            <div>
              <p className="font-bold text-lg">Government of India Approved Recruitment Agency</p>
              <p className="text-green-100 text-sm">MEA Registration Certificate: {MEA_REG}</p>
            </div>
            <Award className="w-8 h-8 flex-shrink-0 hidden md:block" aria-hidden="true" />
          </div>
        </div>
      </div>

      {/* HERO INTRO */}
      <section className="py-16 bg-white" aria-labelledby="ee-intro">
        <div className="container mx-auto px-6 max-w-5xl">
          <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Eastern Europe Corridor · 2026</span>
          <h2 id="ee-intro" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-6">
            Trusted Eastern Europe Recruitment Agency in India
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Eastern Europe is the EU&rsquo;s busiest skilled-labour import market in 2026. Poland alone closed 2025 with ~<strong>1.29 million foreign workers</strong> on its labour rolls, Russia projects continued recruitment of <strong>up to 1 million Indian workers</strong> through 2026, and Czechia, Slovakia, Romania and Hungary are running record vacancies across manufacturing, construction, automotive and logistics. <Link to="/" className="text-zafco-blue font-semibold underline decoration-2 underline-offset-2 hover:text-amber-600">ZAFCO Human Resource Management</Link> is your MEA-licensed <strong>Eastern Europe Recruitment Agency in India</strong> — built for employers running multi-country projects or evaluating Russia vs. Poland vs. emerging EU markets on a single sourcing partner.
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            With <strong>35+ years</strong> of overseas recruitment and active corridors across the region, ZAFCO delivers unified deployment: one requirement brief, one sourcing pipeline, country-specific work-permit and visa handling, and one accountable Eastern Europe desk. This hub sits inside our broader{' '}
            <Link to="/international-manpower-recruitment-europe-russia-israel-saudi-arabia" className="text-zafco-blue font-semibold underline decoration-2 underline-offset-2 hover:text-amber-600">
              International Manpower Recruitment programme
            </Link>{' '}covering Saudi Arabia, Europe, Russia and Israel.
          </p>
          <div className="flex flex-wrap gap-3 mt-2">
            <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90" data-testid="ee-hero-cta-multi-country-quote">
              <Link to={`/contact${contactQuery}-multi-country-quote`}>
                <ArrowRight className="w-4 h-4 mr-2" aria-hidden="true" /> Request Multi-Country Quote
              </Link>
            </Button>
            <Button asChild variant="outline" className="border-zafco-blue text-zafco-blue hover:bg-zafco-blue/5" data-testid="ee-hero-cta-compare">
              <a href="#country-comparison">Compare Countries</a>
            </Button>
            <Button asChild variant="outline" className="border-amber-500 text-amber-400 hover:bg-amber-500/10" data-testid="ee-hero-cta-regional">
              <a href={`mailto:${PRIMARY_EMAIL}?subject=Eastern%20Europe%20recruitment%20enquiry`}>
                <Mail className="w-4 h-4 mr-2" aria-hidden="true" /> Contact Regional Team
              </a>
            </Button>
          </div>
        </div>
      </section>

      {/* WHY EASTERN EUROPE */}
      <section className="py-16 bg-slate-50 border-t border-slate-200" aria-labelledby="why-ee">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 id="why-ee" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-4">
            Why Eastern Europe for skilled manpower recruitment?
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6">
            Eastern Europe&rsquo;s combined labour deficit is the largest single sourcing opportunity in the EU and CIS today. Poland has the EU&rsquo;s lowest unemployment, Russia faces a multi-million-worker shortfall driven by demographic decline, and Czechia, Slovakia and Romania are running back-to-back industrial expansions backed by EU recovery funds and inward FDI. For employers, the regional opportunity is twofold: (1) <strong>cost efficiency</strong>, with Indian sourcing delivering 25–40% lower fully-loaded workforce cost vs. Western EU alternatives, and (2) <strong>scalable supply</strong>, with our pan-India network capable of mobilizing simultaneously across multiple corridors.
          </p>
          <p className="text-gray-700 leading-relaxed mb-6">
            Critically, Poland and the EU markets share a similar Type A / Single Permit / EU Blue Card framework that ZAFCO already operates fluently — meaning a single agency engagement can serve a Polish primary mandate plus rolling expansion into Czechia, Slovakia or Romania without restarting compliance from scratch. For employers with regional headquarters in Warsaw or Prague managing distributed operations across the region, this is the most efficient procurement model. See our dedicated{' '}
            <Link to="/russia-recruitment-agency-india" className="text-zafco-blue font-semibold underline decoration-2 underline-offset-2 hover:text-amber-600">
              Russia Manpower Recruitment
            </Link>
            {' '}and{' '}
            <Link to="/poland-recruitment-agency-india" className="text-zafco-blue font-semibold underline decoration-2 underline-offset-2 hover:text-amber-600">
              Poland Recruitment Agency
            </Link>
            {' '}pages for country-specific operational detail.
          </p>
        </div>
      </section>

      {/* COVERAGE OVERVIEW */}
      <section className="py-16 bg-white" aria-labelledby="ee-coverage">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 id="ee-coverage" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-4">
            ZAFCO&rsquo;s Eastern Europe recruitment coverage
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            Six active or emerging corridors — Russia, Poland, Czechia, Slovakia, Romania and Hungary — managed by a single MEA-licensed Eastern Europe desk in Delhi. Multi-country mandates run in parallel pipelines with country-specific compliance, while pan-Eastern-Europe employer accounts get a unified service-level agreement and consolidated reporting across all destinations.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              ['Russia', 'Active corridor — long-cycle EPC, plant erection, oil & gas'],
              ['Poland', 'Active corridor — logistics, manufacturing, construction'],
              ['Czechia', 'Emerging — automotive, manufacturing, engineering'],
              ['Slovakia', 'Emerging — electronics, automotive, industrial'],
              ['Romania', 'Emerging — EU-funded infrastructure, logistics'],
              ['Hungary', 'Emerging — automotive Tier-1 supply, manufacturing'],
            ].map(([c, body]) => (
              <div key={c} className="bg-slate-50 rounded-2xl p-5 border border-slate-200 hover:border-amber-400 transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <Globe2 className="w-5 h-5 text-zafco-blue" aria-hidden="true" />
                  <h3 className="font-bold text-zafco-blue">{c}</h3>
                </div>
                <p className="text-sm text-gray-600">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RUSSIA SUMMARY */}
      <section className="py-16 bg-slate-50" aria-labelledby="ee-russia">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 id="ee-russia" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-4">
            Russia manpower recruitment
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Russia is positioned as the region&rsquo;s deepest long-cycle industrial corridor. The Russian government&rsquo;s official 2025 quota allocated <strong>71,817 work permits to Indian nationals</strong>, with longer-range projections of up to one million Indian workers absorbed through 2025–26 to address shortfalls in metallurgy, construction, logistics and retail. ZAFCO&rsquo;s Russia desk operates a 30–45 day mobilization window, an in-house trade-testing infrastructure for welders, fitters, CNC operators and structural trades, and full National Visa (Type D) plus work-permit handling through the Russian Embassy in Delhi.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            The highest-demand verticals are <strong>construction</strong> (masons, carpenters, steel fixers, scaffolders), <strong>manufacturing</strong> (machine operators, CNC, assembly), <strong>oil &amp; gas</strong> (pipe fitters, instrument technicians, fabricators), <strong>welding</strong> (MIG, TIG, ARC, 6G) and <strong>engineering</strong> (civil, mechanical, electrical, QA/QC). The 2026 streamlined &quot;Skilled Visa&quot; pathway materially shortens deployment timelines for technical specialists.
          </p>
          <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90 mt-2" data-testid="ee-russia-cta">
            <Link to="/russia-recruitment-agency-india">
              View full Russia recruitment page <ArrowUpRight className="w-4 h-4 ml-1" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </section>

      {/* POLAND SUMMARY */}
      <section className="py-16 bg-white" aria-labelledby="ee-poland">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 id="ee-poland" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-4">
            Poland manpower recruitment
          </h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Poland closed 2025 with approximately <strong>1.29 million foreign workers</strong> on its labour rolls — the EU&rsquo;s largest foreign-labour pool by absolute count — and India is among the fastest-growing origin countries. Polish employers operate on a Type A / Single Permit / EU Blue Card framework, with the 2026 statutory minimum wage of <strong>PLN 4,806/month</strong> mandatorily payable to permit-holders. ZAFCO&rsquo;s Poland desk runs voivodeship-aware filing across Mazowieckie, Wielkopolskie, Śląskie, Małopolskie and Dolnośląskie regions, with end-to-end Type D National Visa processing through the Polish Embassy in Delhi.
          </p>
          <p className="text-gray-700 leading-relaxed mb-4">
            Poland&rsquo;s flagship demand sector is <strong>logistics &amp; warehousing</strong> — driven by the country&rsquo;s emergence as Europe&rsquo;s e-commerce fulfilment hub for Amazon, Zalando, Hermes and DHL. ZAFCO is one of the few Indian agencies running dedicated warehouse-operative, order-picker, packer, forklift-operator and inventory-assistant sourcing pipelines at the scale Polish 3PLs require, plus full manufacturing, construction, welding and engineering coverage.
          </p>
          <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90 mt-2" data-testid="ee-poland-cta">
            <Link to="/poland-recruitment-agency-india">
              View full Poland recruitment page <ArrowUpRight className="w-4 h-4 ml-1" aria-hidden="true" />
            </Link>
          </Button>
        </div>
      </section>

      {/* EMERGING MARKETS */}
      <section className="py-16 bg-slate-50" aria-labelledby="ee-emerging">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="ee-emerging" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-4">
            Emerging Eastern European markets
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8 max-w-3xl">
            Beyond Russia and Poland, four EU markets are actively absorbing skilled Indian labour under the EU Work Permit / Employee Card framework. Country-specific commercials and timelines are issued on enquiry — contact our Eastern Europe desk to scope a deployment.
          </p>
          <div className="grid md:grid-cols-2 gap-5">
            {[
              { icon: Factory, country: 'Czechia', body: 'Anchored on automotive (Škoda, Hyundai Nošovice) and Tier-1 manufacturing supply. EU Employee Card framework, 45–60 day typical processing. Highest demand: CNC operators, assembly technicians, mechanical engineers and welders.' },
              { icon: Cog, country: 'Slovakia', body: 'Europe\u2019s densest per-capita car-manufacturing hub (Volkswagen Bratislava, Kia Žilina, Stellantis Trnava) plus electronics-cluster operations. 45–60 day Temporary Residence + work-permit timeline. Demand: assembly, electronics technicians, maintenance.' },
              { icon: Building2, country: 'Romania', body: 'EU recovery-fund infrastructure boom — highways, rail, logistics parks. 50–70 day EU work-permit timeline. Demand: construction trades, structural welders, civil engineers, warehouse operatives at the Bucharest and Cluj logistics belts.' },
              { icon: Briefcase, country: 'Hungary', body: 'Automotive Tier-1 supply (BMW Debrecen, Mercedes Kecskemét, Audi Győr) and battery-gigafactory ramp-up. Single Permit framework. Demand: production workers, machine operators, electrical technicians.' },
            ].map((m) => {
              const Icon = m.icon;
              return (
                <div key={m.country} className="bg-white rounded-2xl p-6 border border-slate-200 hover:border-amber-400 transition-colors">
                  <div className="flex items-start gap-4">
                    <div className="bg-amber-100 text-amber-700 p-2.5 rounded-xl flex-shrink-0">
                      <Icon className="w-5 h-5" aria-hidden="true" />
                    </div>
                    <div>
                      <h3 className="font-bold text-zafco-blue mb-1.5">{m.country}</h3>
                      <p className="text-sm text-gray-600 leading-relaxed">{m.body}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="mt-8">
            <Button asChild className="bg-zafco-blue hover:bg-zafco-blue/90" data-testid="ee-emerging-cta">
              <Link to={`/contact${contactQuery}-emerging-markets`}>
                <ClipboardListIconFallback /> Request quote for Czechia / Slovakia / Romania
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* COMPARISON TABLE */}
      <section className="py-16 bg-white" aria-labelledby="country-comparison">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="country-comparison" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-4">
            Eastern Europe country comparison
          </h2>
          <p className="text-gray-700 leading-relaxed mb-6 max-w-3xl">
            Side-by-side reference for evaluating destination markets. Cost-advantage figures compare Indian-sourced manpower against Western European labour at fully-loaded employment cost. Processing times vary by voivodeship (Poland), regional office (Russia) and immigration-office workload (EU markets).
          </p>
          <div className="overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm md:text-[15px]">
              <thead className="bg-zafco-blue text-white">
                <tr>
                  <th className="text-left px-4 py-3 font-semibold">Country</th>
                  <th className="text-left px-4 py-3 font-semibold">Work visa / permit</th>
                  <th className="text-left px-4 py-3 font-semibold">Processing</th>
                  <th className="text-left px-4 py-3 font-semibold">Top demand sectors</th>
                  <th className="text-left px-4 py-3 font-semibold">2025–26 signal</th>
                  <th className="text-left px-4 py-3 font-semibold">Cost adv.*</th>
                </tr>
              </thead>
              <tbody>
                {COUNTRY_COMPARISON.map((row, idx) => (
                  <tr key={row.country} className={idx % 2 ? 'bg-slate-50' : 'bg-white'}>
                    <td className="px-4 py-3 font-semibold text-zafco-blue">{row.country}</td>
                    <td className="px-4 py-3 text-gray-700">{row.visa}</td>
                    <td className="px-4 py-3 text-gray-700">{row.timeline}</td>
                    <td className="px-4 py-3 text-gray-700">{row.sectors}</td>
                    <td className="px-4 py-3 text-gray-700">{row.signal}</td>
                    <td className="px-4 py-3 font-semibold text-amber-400">{row.cost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-500 mt-3 italic">
            *Cost advantage compared to Western European labour markets. EU Blue Card requires regional reference-salary threshold (e.g. PLN 11,700 gross/month for Poland 2026). Russian deployment requires confirmed Russian-employer job offer.
          </p>
        </div>
      </section>

      {/* UNIFIED PROCESS */}
      <section className="py-16 bg-slate-50" aria-labelledby="ee-process">
        <div className="container mx-auto px-6 max-w-5xl">
          <h2 id="ee-process" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-4">
            Unified Eastern Europe recruitment process
          </h2>
          <p className="text-gray-700 leading-relaxed mb-8">
            One nine-stage process, applied uniformly across the corridor with country-specific work-permit and visa handoffs at stages 7–8. Multi-country mandates run parallel pipelines with shared sourcing, screening and trade-testing infrastructure, country-bifurcating only at the documentation stage.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              ['01', 'Requirement Analysis', 'Country split, role mix, headcount, timeline'],
              ['02', 'Sourcing', 'Pan-India database, regional centres activated'],
              ['03', 'Screening', 'CV review, English screen, technical questions'],
              ['04', 'Trade Testing', 'In-house centres — welding, CNC, forklift, trades'],
              ['05', 'Client Interview', 'Live video or in-person, per-country panel'],
              ['06', 'Medical Examination', 'Designated centres aligned to destination'],
              ['07', 'Work Permit', 'Country-specific: Type A (PL), Type D (RU), EU permit (CZ/SK/RO)'],
              ['08', 'Visa Processing', 'Polish, Russian, Czech, Slovak, Romanian embassies in Delhi'],
              ['09', 'Mobilization', 'Pre-departure briefing, travel, post-arrival 30-day monitoring'],
            ].map(([n, name, body]) => (
              <div key={n} className="bg-white border border-slate-200 rounded-xl p-4">
                <div className="text-amber-600 text-sm font-bold tracking-wider">{n}</div>
                <div className="font-semibold text-zafco-blue mt-1">{name}</div>
                <div className="text-xs text-gray-600 mt-1">{body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY PARTNER + FINAL CTA */}
      <section className="py-16 bg-gradient-to-br from-zafco-blue to-slate-900 text-white" aria-labelledby="why-partner">
        <div className="container mx-auto px-6 max-w-6xl">
          <h2 id="why-partner" className="text-3xl md:text-4xl font-bold font-syne mb-3">
            Why partner with ZAFCO — your Eastern Europe Recruitment Agency in India
          </h2>
          <p className="text-slate-300 mb-8 max-w-3xl">
            One MEA-licensed agency, six active or emerging corridors, a single dedicated regional desk, and 35+ years of overseas recruitment operational discipline.
          </p>
          <ul className="grid md:grid-cols-2 gap-3 mb-10">
            {[
              ['Multi-country expertise', 'Active mandates running concurrently across Russia, Poland and EU emerging markets'],
              ['Unified management', 'Single contract, single accountability, country-specific compliance handled internally'],
              ['EU compliance discipline', 'Type A · Single Permit · EU Blue Card · Employee Card frameworks all in-house'],
              ['Speed', '30–70 day end-to-end mobilization depending on destination'],
              ['Quality guarantee', 'Multi-stage screening — trade-test, English screen, client interview, medical'],
              ['Cost efficiency', '25–40% lower fully-loaded workforce cost vs. Western EU alternatives'],
            ].map(([t, b]) => (
              <li key={t} className="flex items-start gap-3 bg-slate-800/40 p-4 rounded-xl border border-slate-700/60">
                <CheckCircle className="w-5 h-5 text-amber-400 mt-1 flex-shrink-0" aria-hidden="true" />
                <div>
                  <div className="font-semibold text-white">{t}</div>
                  <div className="text-slate-300 text-sm mt-0.5">{b}</div>
                </div>
              </li>
            ))}
          </ul>
          <div className="grid md:grid-cols-2 gap-4">
            <Link
              to={`/contact${contactQuery}-final-employer`}
              className="bg-amber-500 text-slate-900 rounded-2xl p-6 font-semibold flex items-center justify-between hover:bg-amber-400 transition-colors"
              data-testid="ee-final-cta-employer"
            >
              <span>
                <span className="block text-xs uppercase tracking-widest text-slate-700">For Eastern European employers</span>
                <span className="text-lg">Request multi-country quote</span>
              </span>
              <ArrowUpRight className="w-6 h-6" aria-hidden="true" />
            </Link>
            <a
              href={`tel:${PRIMARY_PHONE.replace(/[^+\d]/g, '')}`}
              className="bg-slate-800/60 border border-slate-700 text-white rounded-2xl p-6 font-semibold flex items-center justify-between hover:bg-slate-800 transition-colors"
              data-testid="ee-final-cta-phone"
            >
              <span>
                <span className="block text-xs uppercase tracking-widest text-slate-400">Speak to our Eastern Europe desk</span>
                <span className="text-lg">{PRIMARY_PHONE}</span>
              </span>
              <Phone className="w-6 h-6 text-amber-400" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      {/* CONTACT FORM */}
      <section className="py-16 bg-white" id="contact" aria-labelledby="ee-contact-form">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-8">
            <span className="text-amber-600 font-semibold text-sm tracking-widest uppercase">Talk to our Eastern Europe desk</span>
            <h2 id="ee-contact-form" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mt-2 mb-4">
              Next steps for Eastern Europe recruitment
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Share your manpower requirement — destination country, role mix, headcount, deployment window. Our Eastern Europe desk responds within one working day with a comparative sourcing plan, country-specific timelines and indicative commercials.
            </p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-xl p-6 md:p-8 max-w-3xl mx-auto" data-theme="light">
            <ConsultationForm sourceTag="eastern-europe-recruitment-india" />
          </div>
        </div>
      </section>

      {/* FAQS */}
      <section className="py-16 bg-slate-50" aria-labelledby="faqs-ee">
        <div className="container mx-auto px-6 max-w-4xl">
          <h2 id="faqs-ee" className="text-3xl md:text-4xl font-bold text-zafco-blue font-syne mb-3">
            Frequently asked questions
          </h2>
          <div className="space-y-4 mt-6">
            {FAQS.map((f, idx) => (
              <details key={f.q} className="bg-white rounded-xl border border-slate-200 p-5 group" data-testid={`ee-faq-${idx}`}>
                <summary className="cursor-pointer font-semibold text-zafco-blue list-none flex items-start justify-between gap-4">
                  <span>{f.q}</span>
                  <span className="text-amber-600 group-open:rotate-45 transition-transform text-xl leading-none">+</span>
                </summary>
                <p className="text-gray-700 mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Lightweight inline icon fallback for the emerging-markets CTA
const ClipboardListIconFallback = () => (
  <FileCheck className="w-4 h-4 mr-2" aria-hidden="true" />
);

export default EasternEuropeRecruitmentIndiaPage;
